import React, { useState, useRef, useCallback, useEffect } from "react";
import {
    GoogleMap,
    LoadScript,
    Marker,
} from "@react-google-maps/api";
import "./index.scss";

const libraries = ["places"];

const mapContainerStyle = {
    width: "100%",
    height: "400px",
};

const defaultCenter = {
    lat: 42.876, // Bishkek default
    lng: 74.607,
};

export default function AddressModal({
    isOpen,
    isEdit = false,
    onClose,
    onSave,
    latitude = null,
    longitude = null,
    initialAddress = "",
    translations = {},
}) {
    // Address chosen via map / autocomplete / geocoding
    const [selectedAddress, setSelectedAddress] = useState( "");
    // Text currently shown in the search input
    const [searchQuery, setSearchQuery] = useState(  "");
    // Purely manual address text, used only if user can't find address on map
    const [manualAddress, setManualAddress] = useState(initialAddress || "");
    const [selectedPosition, setSelectedPosition] = useState(null);
    const [mapCenter, setMapCenter] = useState(defaultCenter);
    const [autocompleteSuggestions, setAutocompleteSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const searchOverlayRef = useRef(null);
    const sessionTokenRef = useRef(null);

    const geocoderRef = useRef(null);
    const mapRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            // Initialize session token for Places API
            if (window.google?.maps?.places && !sessionTokenRef.current) {
                sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
            }

            // Prioritize latitude/longitude over initialAddress
            if (latitude !== null && longitude !== null && 
                typeof latitude === 'number' && typeof longitude === 'number' &&
                !isNaN(latitude) && !isNaN(longitude)) {
                
                const position = { lat: latitude, lng: longitude };
                setSelectedPosition(position);
                setMapCenter(position);

                // Use reverse geocoding to get address from coordinates
                if (window.google?.maps) {
                    if (!geocoderRef.current) {
                        geocoderRef.current = new window.google.maps.Geocoder();
                    }

                    geocoderRef.current.geocode({ location: position }, (results, status) => {
                        if (status === "OK" && results && results[0]) {
                            const address = results[0].formatted_address;
                            setSelectedAddress(address);
                            // setSearchQuery(address);
                        } else {
                            // If geocoding fails, use initialAddress or empty string
                            const initial = initialAddress || "";
                            setSelectedAddress(initial);
                            setManualAddress(initial)
                            // setSearchQuery(initial);
                        }
                    });
                } else {
                    // If Google Maps not loaded yet, set position and wait for it to load
                    const initial = initialAddress || "";
                    setSelectedAddress(initial);
                    setManualAddress(initial)
                    // setSearchQuery(initial);
                }
            } else {
                // No coordinates provided, use initialAddress
                const initial = initialAddress || "";
                setSelectedAddress(initial);
                setManualAddress(initial)
                // setSearchQuery(initial);
            }
            // Do NOT touch manualAddress here; it is for the separate manual field.
        } else {
            // Reset session token when modal closes
            sessionTokenRef.current = null;
        }
    }, [isOpen, initialAddress, latitude, longitude]);


    const handleMapClick = useCallback(
        (event) => {
            if (!geocoderRef.current) {
                geocoderRef.current = new window.google.maps.Geocoder();
            }

            const lat = event.latLng.lat();
            const lng = event.latLng.lng();
            const position = { lat, lng };

            setSelectedPosition(position);
            setMapCenter(position);

            // Zoom to clicked location
            if (mapRef.current) {
                mapRef.current.setCenter(position);
                mapRef.current.setZoom(15);
            }

            geocoderRef.current.geocode({ location: position }, (results, status) => {
                if (status === "OK" && results && results[0]) {
                    const address = results[0].formatted_address;
                    setSelectedAddress(address);
                    setSearchQuery(address);
                    setManualAddress(address)
                }
            });
        },
        []
    );

    const geocodeAddress = useCallback(
        (address) => {
            if (!geocoderRef.current) {
                geocoderRef.current = new window.google.maps.Geocoder();
            }

            geocoderRef.current.geocode({ address: address }, (results, status) => {
                if (status === "OK" && results && results[0]) {
                    const location = results[0].geometry.location;
                    const lat = location.lat();
                    const lng = location.lng();
                    const formattedAddress = results[0].formatted_address;

                    setSelectedAddress(formattedAddress);
                    setSearchQuery(formattedAddress);
                    setSelectedPosition({ lat, lng });
                    setMapCenter({ lat, lng });

                    // Zoom to the location
                    if (mapRef.current) {
                        if (results[0].geometry.viewport) {
                            try {
                                const viewport = results[0].geometry.viewport;
                                // Check if viewport has the required methods
                                if (viewport && typeof viewport.getSouthWest === 'function' && typeof viewport.getNorthEast === 'function') {
                                    // Create a proper bounds object from viewport
                                    const bounds = new window.google.maps.LatLngBounds(
                                        viewport.getSouthWest(),
                                        viewport.getNorthEast()
                                    );
                                    mapRef.current.fitBounds(bounds);
                                } else {
                                    // If viewport is not a proper LatLngBounds, just center and zoom
                                    mapRef.current.setCenter({ lat, lng });
                                    mapRef.current.setZoom(15);
                                }
                            } catch (error) {
                                console.error("Error fitting bounds:", error);
                                // Fallback if viewport is invalid
                                mapRef.current.setCenter({ lat, lng });
                                mapRef.current.setZoom(15);
                            }
                        } else {
                            mapRef.current.setCenter({ lat, lng });
                            mapRef.current.setZoom(15);
                        }
                    }
                    setShowSuggestions(false);
                } else {
                    // If geocoding fails, try Places API
                    const placesService = new window.google.maps.places.PlacesService(
                        document.createElement("div")
                    );
                    const request = {
                        query: address,
                        fields: ["formatted_address", "geometry"],
                    };

                    placesService.findPlaceFromQuery(request, (places, placesStatus) => {
                        if (
                            placesStatus === window.google.maps.places.PlacesServiceStatus.OK &&
                            places &&
                            places.length > 0
                        ) {
                            const place = places[0];
                            if (place.geometry && place.geometry.location) {
                                const location = place.geometry.location;
                                const lat = location.lat();
                                const lng = location.lng();

                                const finalAddress = place.formatted_address || address;
                                setSelectedAddress(finalAddress);
                                setSearchQuery(finalAddress);
                                setSelectedPosition({ lat, lng });
                                setMapCenter({ lat, lng });

                                if (mapRef.current) {
                                    if (place.geometry.viewport) {
                                        try {
                                            const viewport = place.geometry.viewport;
                                            // Check if viewport has the required methods
                                            if (viewport && typeof viewport.getSouthWest === 'function' && typeof viewport.getNorthEast === 'function') {
                                                // Create a proper bounds object from viewport
                                                const bounds = new window.google.maps.LatLngBounds(
                                                    viewport.getSouthWest(),
                                                    viewport.getNorthEast()
                                                );
                                                mapRef.current.fitBounds(bounds);
                                            } else {
                                                // If viewport is not a proper LatLngBounds, just center and zoom
                                                mapRef.current.setCenter({ lat, lng });
                                                mapRef.current.setZoom(15);
                                            }
                                        } catch (error) {
                                            console.error("Error fitting bounds:", error);
                                            // Fallback if viewport is invalid
                                            mapRef.current.setCenter({ lat, lng });
                                            mapRef.current.setZoom(15);
                                        }
                                    } else {
                                        mapRef.current.setCenter({ lat, lng });
                                        mapRef.current.setZoom(15);
                                    }
                                }
                            }
                        }
                        setShowSuggestions(false);
                    });
                }
            });
        },
        []
    );

    const handleSearchChange = useCallback(async (e) => {
        const query = e.target.value;
        setSearchQuery(query);

        if (!query.trim()) {
            setAutocompleteSuggestions([]);
            setShowSuggestions(false);
            setSelectedAddress("");
            setSelectedPosition(null);
            return;
        }

        if (!window.google?.maps?.places) {
            return;
        }

        // Initialize session token if not already initialized
        if (!sessionTokenRef.current && window.google.maps.places.AutocompleteSessionToken) {
            sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
        }

        try {
            // Try using AutocompleteSuggestion API (newer API)
            if (window.google.maps.places.AutocompleteSuggestion) {
                const { suggestions } =
                    await window.google.maps.places.AutocompleteSuggestion
                        .fetchAutocompleteSuggestions({
                            input: query,
                            sessionToken: sessionTokenRef.current,
                            includedPrimaryTypes: ["street_address", "route"],
                        });

                setAutocompleteSuggestions(suggestions || []);
                setShowSuggestions(Boolean(suggestions?.length));
            } else {
                // Fallback to AutocompleteService (older API)
                const service = new window.google.maps.places.AutocompleteService();
                service.getPlacePredictions(
                    {
                        input: query,
                        types: ["address"],
                    },
                    (predictions, status) => {
                        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
                            const formattedSuggestions = predictions.map((prediction) => ({
                                placePrediction: {
                                    placeId: prediction.place_id,
                                    text: { text: prediction.description },
                                    description: prediction.description,
                                },
                            }));
                            setAutocompleteSuggestions(formattedSuggestions);
                            setShowSuggestions(true);
                        } else {
                            setAutocompleteSuggestions([]);
                            setShowSuggestions(false);
                        }
                    }
                );
            }
        } catch (error) {
            console.error("Error fetching autocomplete suggestions:", error);
            setAutocompleteSuggestions([]);
            setShowSuggestions(false);
        }
    }, []);


    const handleSearchKeyPress = useCallback(
        (e) => {
            if (e.key === "Enter" && searchQuery.trim()) {
                e.preventDefault();
                geocodeAddress(searchQuery.trim());
            }
        },
        [searchQuery, geocodeAddress]
    );

    const handleSuggestionClick = useCallback(async (suggestion) => {
        const placeId = suggestion?.placePrediction?.placeId;

        if (!placeId || typeof placeId !== "string") {
            console.error("Invalid placeId:", placeId, suggestion);
            return;
        }

        const place = new window.google.maps.places.Place({ id: placeId });

        await place.fetchFields({
            fields: ["formattedAddress", "location", "viewport"],
        });

        if (!place.location) return;

        const lat = place.location.lat();
        const lng = place.location.lng();
        const address = place.formattedAddress;

        setSelectedAddress(address);
        setSearchQuery(address);
        setManualAddress(address);
        setSelectedPosition({ lat, lng });
        setMapCenter({ lat, lng });
        setShowSuggestions(false);

        if (mapRef.current) {
            if (place.viewport) {
                try {
                    const viewport = place.viewport;
                    // Check if viewport has the required methods
                    if (viewport && typeof viewport.getSouthWest === 'function' && typeof viewport.getNorthEast === 'function') {
                        // Create a proper bounds object from viewport
                        const bounds = new window.google.maps.LatLngBounds(
                            viewport.getSouthWest(),
                            viewport.getNorthEast()
                        );
                        mapRef.current.fitBounds(bounds);
                    } else {
                        // If viewport is not a proper LatLngBounds, just center and zoom
                        mapRef.current.setCenter({ lat, lng });
                        mapRef.current.setZoom(15);
                    }
                } catch (error) {
                    console.error("Error fitting bounds:", error);
                    // Fallback if viewport is invalid
                    mapRef.current.setCenter({ lat, lng });
                    mapRef.current.setZoom(15);
                }
            } else {
                mapRef.current.setCenter({ lat, lng });
                mapRef.current.setZoom(15);
            }
        }
    }, []);


    const handleSave = () => {
        // Prefer a map/Google-selected address; fall back to manual text
        const addressToSave =  manualAddress || searchQuery || selectedAddress || "";
        if (onSave) {
            onSave({
                address: addressToSave,
                latitude: selectedPosition?.lat ?? null,
                longitude: selectedPosition?.lng ?? null,
            });
        }
        onClose();
    };

    const handleManualAddressChange = (e) => {
        setManualAddress(e.target.value);
    };

    // Close suggestions when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                searchOverlayRef.current &&
                !searchOverlayRef.current.contains(event.target)
            ) {
                setShowSuggestions(false);
            }
        };

        if (showSuggestions) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showSuggestions]);

    if (!isOpen) return null;

    return (
        <div className="address-modal">
            <div className="address-modal__bg" onClick={onClose}></div>
            <div className="address-modal__content">
                <div className="address-modal__header">
                    <div className="address-modal__title">
                        {isEdit ? translations?.["main.edit_project_address.headers.edit_address"] :
                        translations?.["main.edit_project_address.headers.add_address"]
                        || "Add address"
                        }
                    </div>
                    <div className="address-modal__close" onClick={onClose}>
                        <img src="/assets/icons/close.svg" alt="close" />
                    </div>
                </div>

                <div className="address-modal__body">

                    <div className="address-modal__map">
                        <LoadScript
                            googleMapsApiKey={'AIzaSyDthSzSl8bPrdEc0Mmb9RrVH62prk1trbc'}
                            libraries={libraries}
                        >
                            <div className="address-modal__map-wrapper">
                                <div className="address-modal__search-overlay" ref={searchOverlayRef}>
                                    <div className="address-modal__search-input-wrapper">
                                        <img
                                            src="/assets/icons/search.svg"
                                            alt="search"
                                            className="address-modal__search-icon"
                                        />
                                        <input
                                            type="text"
                                            placeholder={
                                                translations?.["main.edit_project_address.placeholder.search_address"] ||
                                                "Search address"
                                            }
                                            value={searchQuery}
                                            onChange={handleSearchChange}
                                            onKeyPress={handleSearchKeyPress}
                                            onFocus={() => {
                                                if (autocompleteSuggestions.length > 0) {
                                                    setShowSuggestions(true);
                                                }
                                            }}
                                            className="address-modal__search-input"
                                        />
                                    </div>
                                    {showSuggestions && autocompleteSuggestions.length > 0 && (
                                        <div className="address-modal__suggestions">
                                            {autocompleteSuggestions.map((suggestion, index) => (
                                                <div
                                                    key={index}
                                                    className="address-modal__suggestion-item"
                                                    onClick={() => handleSuggestionClick(suggestion)}
                                                >
                                                    {suggestion.placePrediction?.text?.text || 
                                                     suggestion.placePrediction?.description || 
                                                     "Address"}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <GoogleMap
                                    mapContainerStyle={mapContainerStyle}
                                    center={mapCenter}
                                    zoom={selectedPosition ? 15 : 7}
                                    onClick={(e) => {
                                        handleMapClick(e);
                                        setShowSuggestions(false);
                                    }}
                                    options={{
                                        disableDefaultUI: false,
                                        zoomControl: true,
                                        mapTypeControl: false,
                                        fullscreenControl: false,
                                    }}
                                    onLoad={(map) => {
                                        mapRef.current = map;
                                        if (!geocoderRef.current && window.google) {
                                            geocoderRef.current = new window.google.maps.Geocoder();
                                        }
                                        
                                        // If we have coordinates but haven't geocoded yet, do it now
                                        if (latitude !== null && longitude !== null && 
                                            typeof latitude === 'number' && typeof longitude === 'number' &&
                                            !isNaN(latitude) && !isNaN(longitude) &&
                                            !selectedAddress && geocoderRef.current) {
                                            const position = { lat: latitude, lng: longitude };
                                            setSelectedPosition(position);
                                            setMapCenter(position);
                                            
                                            geocoderRef.current.geocode({ location: position }, (results, status) => {
                                                if (status === "OK" && results && results[0]) {
                                                    const address = results[0].formatted_address;
                                                    setSelectedAddress(address);
                                                    setSearchQuery(address);
                                                }
                                            });
                                            
                                            map.setCenter(position);
                                            map.setZoom(15);
                                        } else if (selectedPosition) {
                                            // Ensure map is centered on selected position
                                            map.setCenter(selectedPosition);
                                            map.setZoom(15);
                                        }
                                    }}
                                >
                                    {selectedPosition && (
                                        <Marker position={selectedPosition} />
                                    )}
                                </GoogleMap>
                            </div>
                        </LoadScript>
                    </div>

                    <div className="address-modal__manual">
                        <p className="address-modal__manual-label">
                            {translations?.["main.edit_project_address.titles.cant_find_the_address"] ||
                                "Can't find the address? Enter manually"}
                        </p>
                        <div className="address-modal__manual-input-wrapper">
                            <input
                                type="text"
                                placeholder={
                                    translations?.["main.edit_project_address.placeholder.enter_manually_address"] ||
                                    "Enter manually address"
                                }
                                value={manualAddress}
                                onChange={handleManualAddressChange}
                                className="address-modal__manual-input"
                            />
                            <img
                                src="/assets/icons/map-outline.svg"
                                alt="location"
                                className="address-modal__manual-icon"
                            />
                        </div>
                    </div>
                </div>

                <div className="address-modal__footer">
                    <button
                        className="address-modal__btn address-modal__btn--cancel"
                        onClick={onClose}
                    >
                        {translations?.["main.edit_project_address.actions.cancel"] || "Cancel"}
                    </button>
                    <button
                        className="address-modal__btn address-modal__btn--save"
                        onClick={handleSave}
                    >
                        {translations?.["main.edit_project_address.actions.save"] || "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}

